import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  ArrowRight,
  ArrowLeft,
  Check,
  Loader2,
  User,
  FileText,
  GraduationCap,
  Globe,
  Plane,
  Ticket,
  Home,
  DollarSign,
  Briefcase,
} from 'lucide-react';
import { partnerAPI } from '../../services/api';
import { toast } from 'sonner';
import ProfileAssessmentModal from '../services/hub/ProfileAssessmentModal';
import ServiceFormModal from '../services/hub/ServiceFormModal';

/**
 * Unified service-apply flow for partner (rep3) dashboard.
 * Partner selects a student, then a service, then fills the same form the
 * student would see. Submits through partnerAPI so backend stamps
 * representativeId / representativeLevel=3 / createdBy=rep3 /
 * interactionMode=rep-counselor automatically.
 */

// EXACT service list requested — maps display label → backend serviceType +
// ServiceFormModal slug (null slug means use ProfileAssessmentModal instead)
export const PARTNER_SERVICE_CATALOG = [
  {
    serviceType: 'PROFILE_ASSESSMENT',
    label: 'Profile Assessment',
    description: 'Evaluate student profile and study-abroad readiness',
    slug: null, // uses ProfileAssessmentModal
    icon: User,
    color: 'from-indigo-500 to-indigo-600',
    bg: 'bg-indigo-50',
    text: 'text-indigo-600',
  },
  {
    serviceType: 'UNIVERSITY_SHORTLISTING',
    label: 'Preapplication Support',
    description: 'Pre-application guidance and university shortlisting',
    slug: 'preapplication-support',
    icon: GraduationCap,
    color: 'from-blue-500 to-blue-600',
    bg: 'bg-blue-50',
    text: 'text-blue-600',
  },
  {
    serviceType: 'APPLICATION_ASSISTANCE',
    label: 'Apply University',
    description: 'End-to-end university application support',
    slug: 'apply-university',
    icon: FileText,
    color: 'from-purple-500 to-purple-600',
    bg: 'bg-purple-50',
    text: 'text-purple-600',
  },
  {
    serviceType: 'VISA_GUIDANCE',
    label: 'Visa & Interview Support',
    description: 'Visa processing, document prep and interview coaching',
    slug: 'visa-interview',
    icon: Globe,
    color: 'from-emerald-500 to-emerald-600',
    bg: 'bg-emerald-50',
    text: 'text-emerald-600',
  },
  {
    serviceType: 'SCHOLARSHIP_SEARCH',
    label: 'Ticket & Travel Support',
    description: 'Flight booking, travel planning and departure help',
    slug: 'ticket-travel',
    icon: Ticket,
    color: 'from-amber-500 to-amber-600',
    bg: 'bg-amber-50',
    text: 'text-amber-600',
  },
  {
    serviceType: 'ACCOMMODATION_HELP',
    label: 'Find Accommodation',
    description: 'Housing, dorms and accommodation abroad',
    slug: 'accommodation',
    icon: Home,
    color: 'from-rose-500 to-rose-600',
    bg: 'bg-rose-50',
    text: 'text-rose-600',
  },
  {
    serviceType: 'LOAN_ASSISTANCE',
    label: 'Education Loan',
    description: 'Education loan guidance and documentation',
    slug: 'education-loan',
    icon: DollarSign,
    color: 'from-teal-500 to-teal-600',
    bg: 'bg-teal-50',
    text: 'text-teal-600',
  },
  {
    serviceType: 'PRE_DEPARTURE_ORIENTATION',
    label: 'Find Jobs Abroad',
    description: 'Job search and career placement abroad',
    slug: 'jobs-abroad',
    icon: Briefcase,
    color: 'from-cyan-500 to-cyan-600',
    bg: 'bg-cyan-50',
    text: 'text-cyan-600',
  },
];

export default function PartnerApplyServiceFlow({
  open,
  onOpenChange,
  onCreated,
  // Optional pre-selection (e.g. from a student row or marketplace card)
  initialStudentId = '',
  initialServiceType = '',
}) {
  // Step: 0 = student, 1 = service, 2 = form
  const [step, setStep] = useState(0);
  const [students, setStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [studentId, setStudentId] = useState(initialStudentId);
  const [service, setService] = useState(
    PARTNER_SERVICE_CATALOG.find((s) => s.serviceType === initialServiceType) || null
  );
  const [submitting, setSubmitting] = useState(false);

  // Reset on open
  useEffect(() => {
    if (open) {
      setStep(initialStudentId && initialServiceType ? 2 : initialStudentId ? 1 : 0);
      setStudentId(initialStudentId);
      setService(
        PARTNER_SERVICE_CATALOG.find((s) => s.serviceType === initialServiceType) || null
      );
      if (!students.length) loadStudents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const loadStudents = async () => {
    setLoadingStudents(true);
    try {
      const res = await partnerAPI.getStudents({ limit: 200 });
      setStudents(res.data || []);
    } catch (err) {
      toast.error('Failed to load students');
    } finally {
      setLoadingStudents(false);
    }
  };

  const selectedStudent = students.find(
    (s) => (s.studentId || s._id) === studentId
  );

  const handleCreate = async (formData = {}) => {
    if (!studentId || !service) {
      toast.error('Missing student or service');
      return;
    }
    setSubmitting(true);
    try {
      await partnerAPI.createServiceRequest({
        studentId,
        serviceType: service.serviceType,
        formData,
        metadata: {
          source: 'partner-dashboard',
          interactionMode: 'rep-counselor',
        },
      });
      toast.success(`${service.label} request created successfully`);
      onOpenChange(false);
      if (onCreated) onCreated();
    } catch (err) {
      console.error('Create service request failed:', err);
      toast.error(
        err.response?.data?.error ||
          err.response?.data?.message ||
          'Failed to create service request'
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ── Profile Assessment fork: use reusable modal with overrides ──────────
  // We render a secondary modal only when step === 2 && slug === null.
  const showProfileAssessment = step === 2 && service && service.slug === null;
  const showServiceForm = step === 2 && service && service.slug;

  const profileSource = async () => {
    // Seed with existing student details so partner sees what's there
    try {
      const res = await partnerAPI.getStudentDetails(studentId);
      const d = res.data || res.student || res;
      const student = d.student || d;
      return {
        student: {
          firstName: student.firstName || student.user?.firstName,
          lastName: student.lastName || student.user?.lastName,
          email: student.email || student.user?.email,
          phone: student.phone || student.user?.phone,
          country: student.country,
          age: student.age,
          currentEducationLevel: student.currentEducationLevel,
          fieldOfStudy: student.fieldOfStudy,
          gpa: student.gpa,
          graduationYear: student.graduationYear,
          institution: student.institution,
          ielts: student.ielts,
          toefl: student.toefl,
          gre: student.gre,
          preferredCountries: student.preferredCountries || [],
          preferredDegreeLevel: student.preferredDegreeLevel,
          budget: student.budget,
          careerGoals: student.careerGoals,
          industry: student.industry,
          workLocation: student.workLocation,
          documents: student.documents || {},
        },
      };
    } catch {
      return { student: {} };
    }
  };

  return (
    <>
      {/* Main Dialog: Step 0 + Step 1 (student + service selection) */}
      <Dialog
        open={open && step !== 2}
        onOpenChange={(v) => {
          if (!v) onOpenChange(false);
        }}
      >
        <DialogContent className="partner-dialog max-w-2xl rounded-[30px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plane className="w-5 h-5 text-[#8D68E8]" />
              New Service Request
            </DialogTitle>
            <DialogDescription>
              {step === 0
                ? 'Select the student you are creating this service for.'
                : 'Choose the service to apply for on behalf of the student.'}
            </DialogDescription>
          </DialogHeader>

          {/* Step indicator */}
          <div className="flex items-center gap-2 my-3">
            {['Student', 'Service', 'Details'].map((label, i) => (
              <React.Fragment key={label}>
                <div
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                    i === step
                      ? 'border border-[#A87DFA]/20 bg-[#A87DFA]/12 text-[#6D49D4]'
                      : i < step
                      ? 'border border-[#A87DFA]/20 bg-[#A87DFA]/10 text-[#6D49D4]'
                      : 'border border-[#A87DFA]/14 bg-white/40 text-slate-500'
                  }`}
                >
                  {i < step ? <Check className="w-3 h-3" /> : <span>{i + 1}</span>}
                  {label}
                </div>
                {i < 2 && <div className="h-px flex-1 bg-[#A87DFA]/16" />}
              </React.Fragment>
            ))}
          </div>

          {/* Step 0: Student selection */}
          {step === 0 && (
            <div className="space-y-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">
                  Student
                </label>
                <Select value={studentId} onValueChange={setStudentId}>
                  <SelectTrigger>
                    <SelectValue
                      placeholder={
                        loadingStudents ? 'Loading students…' : 'Select a student'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {students.map((s) => {
                      const id = s.studentId || s._id;
                      const u = s.user || s;
                      return (
                        <SelectItem key={id} value={id}>
                          {u.firstName} {u.lastName}
                          {u.email ? ` (${u.email})` : ''}
                        </SelectItem>
                      );
                    })}
                    {!loadingStudents && students.length === 0 && (
                      <div className="px-3 py-2 text-sm text-gray-500">
                        No students yet
                      </div>
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" className="partner-secondary-btn rounded-2xl" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button
                  disabled={!studentId}
                  onClick={() => setStep(1)}
                  className="partner-primary-btn rounded-2xl"
                >
                  Next <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 1: Service selection */}
          {step === 1 && (
            <div className="space-y-4">
              {selectedStudent && (
                <div className="rounded-2xl border border-[#A87DFA]/14 bg-white/38 px-3 py-2 text-xs text-slate-500">
                  For:{' '}
                  <span className="font-medium text-slate-700">
                    {selectedStudent.user?.firstName ||
                      selectedStudent.firstName}{' '}
                    {selectedStudent.user?.lastName || selectedStudent.lastName}
                  </span>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-h-[420px] overflow-y-auto pr-1">
                {PARTNER_SERVICE_CATALOG.map((s) => {
                  const Icon = s.icon;
                  const isSelected = service?.serviceType === s.serviceType;
                  return (
                    <Card
                      key={s.serviceType}
                      onClick={() => setService(s)}
                      className={`cursor-pointer rounded-[22px] p-3 transition-all ${
                        isSelected
                          ? 'partner-card border-[#A87DFA]/34 ring-2 ring-[#A87DFA]/10'
                          : 'partner-card-hover border border-[#A87DFA]/16 bg-white/35'
                      }`}
                    >
                      <div className="flex items-start gap-2.5">
                        <div className="flex h-10 w-10 items-center justify-center rounded-2xl border border-[#A87DFA]/16 bg-[linear-gradient(135deg,rgba(168,125,250,0.16),rgba(168,125,250,0.05))]">
                          <Icon className="w-4 h-4 text-[#7A55DF]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900">
                            {s.label}
                          </p>
                          <p className="mt-0.5 line-clamp-2 text-xs text-slate-500">
                            {s.description}
                          </p>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>

              <div className="flex justify-between gap-2 pt-2">
                <Button variant="outline" className="partner-secondary-btn rounded-2xl" onClick={() => setStep(0)}>
                  <ArrowLeft className="w-4 h-4 mr-1" /> Back
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" className="partner-secondary-btn rounded-2xl" onClick={() => onOpenChange(false)}>
                    Cancel
                  </Button>
                  <Button
                    disabled={!service}
                    onClick={() => setStep(2)}
                    className="partner-primary-btn rounded-2xl"
                  >
                    Continue <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Step 2a: Profile Assessment (reuse existing modal) */}
      {showProfileAssessment && (
        <ProfileAssessmentModal
          open={true}
          onOpenChange={(v) => {
            if (!v) {
              // back to service selection rather than closing the whole flow
              setStep(1);
            }
          }}
          onApplySuccess={async () => {}}
          submitLabel="Create Service Request"
          showRepresentativeNameField={false}
          profileSource={profileSource}
          onCustomSubmit={async ({ profilePayload, documents }) => {
            await handleCreate({ ...profilePayload, documents });
          }}
        />
      )}

      {/* Step 2b: Other services — reuse ServiceFormModal */}
      {showServiceForm && (
        <ServiceFormModal
          open={true}
          onOpenChange={(v) => {
            if (!v) setStep(1);
          }}
          serviceSlug={service.slug}
          showRepresentativeNameField={false}
          onSubmit={async (formData) => {
            await handleCreate(formData);
          }}
        />
      )}

      {/* Submitting overlay */}
      {submitting && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-[#A87DFA]/12 backdrop-blur-sm">
          <div className="partner-dialog flex items-center gap-2 rounded-[22px] px-5 py-3 shadow-lg">
            <Loader2 className="w-4 h-4 animate-spin text-[#8D68E8]" />
            <span className="text-sm text-gray-700">Creating request…</span>
          </div>
        </div>
      )}
    </>
  );
}
